import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import SearchableDropdown from '../../common/SearchableDropdown';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Alert, Snackbar, Tooltip, IconButton } from '@mui/material';
import { ArrowBack, Refresh, GetApp } from '@mui/icons-material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY     = '#0d8392';
const PRIMARY_MID = '#1aabb9';
const PRIMARY_LT  = '#e0f7fa';

const fmtN = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
    if (!d) return '-';
    const dt = new Date(d);
    return isNaN(dt) ? '-' : dt.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const STATUS_COLORS = {
    'FULLY PAID': { bg: '#d1fae5', color: '#059669' },
    'PARTIAL':    { bg: '#fef9c3', color: '#92400e' },
};

const iconBtn = {
    color: '#fff',
    '&:hover': { background: 'rgba(255,255,255,0.15)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
    borderRadius: '6px', padding: '4px',
};

const VendorBillWisePaymentDetails = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const { logAudit } = useAuditLog();
    const history = useHistory();

    const today = new Date();
    const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [toDate, setToDate]     = useState(fmt2Date(today));
    const [vendors, setVendors]   = useState([]);
    const [vendorId, setVendorId] = useState('');
    const [data, setData]         = useState([]);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');
    const [fetched, setFetched]   = useState(false);
    const [search, setSearch]     = useState('');

    useEffect(() => {
        if (companyId && token) {
            fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token)
                .then(res => setVendors(res?.response_data || res || []))
                .catch(() => setVendors([]));
        }
    }, [companyId, token]);

    useEffect(() => {
        if (companyId && branchId && token && vendorId && fromDate && toDate) {
            if (new Date(fromDate) > new Date(toDate)) {
                setData([]);
                setFetched(false);
                setError('From Date cannot be greater than To Date');
                return;
            }
            fetchReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorId]);

    const fetchReport = async () => {
        if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
        setLoading(true);
        try {
            const res = await fetchData(
                'PurchaseReportApi',
                `/VendorWiseBillPaymentDetails/${companyId}/${branchId}/${vendorId || 0}/${fromDate}/${toDate}`,
                token
            );
            const rows = res?.response_data || res || [];
            const arr  = Array.isArray(rows) ? rows : [];
            // DEBUG – remove after confirming field names
            if (arr.length) console.log('[VendorBillWisePayment] sample row keys:', Object.keys(arr[0]), 'first rows:', arr.slice(0, 6));
            setData(arr);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch vendor bill wise payment details');
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        if (!search.trim()) return data;
        const q = search.toLowerCase();
        return data.filter(r =>
            (r.SupplierName || '').toLowerCase().includes(q) ||
            (r.BillNo || '').toLowerCase().includes(q) ||
            (r.PaymentTypeName || '').toLowerCase().includes(q) ||
            (r.PaymentStatus || '').toLowerCase().includes(q)
        );
    }, [data, search]);

    // Group rows by PurchaseId.
    // PurchaseId is the authoritative bill identifier from the DB.
    // Rows with no/zero PurchaseId are treated as individual groups.
    // Within each group, bill-level fields are forward-filled from the
    // first row that actually has them (handles APIs that omit them on
    // sub-payment rows).
    const groupedRows = useMemo(() => {
        const groups = [];
        const seen   = new Map(); // key → index in groups[]

        filtered.forEach((r, idx) => {
            const pid = r.PurchaseId;
            // Only merge under a shared PurchaseId when it is a real non-zero value
            const key = (pid && String(pid) !== '0') ? `pid__${pid}` : `unique__${idx}`;

            if (!seen.has(key)) {
                seen.set(key, groups.length);
                groups.push({ key, rows: [{ ...r }] });
            } else {
                const grp  = groups[seen.get(key)];
                // Find the best "base" row — the one that already has BillNo filled
                const base = grp.rows.find(b => b.BillNo && b.BillNo.trim()) || grp.rows[0];
                const filled = {
                    ...r,
                    SupplierName:   r.SupplierName   || base.SupplierName,
                    BillNo:         r.BillNo         || base.BillNo,
                    BillDate:       r.BillDate       || base.BillDate,
                    PaymentDueDate: r.PaymentDueDate || base.PaymentDueDate,
                    BillAmount:     (r.BillAmount != null && r.BillAmount !== '') ? r.BillAmount : base.BillAmount,
                    PaymentStatus:  r.PaymentStatus  || base.PaymentStatus,
                    RN:             r.RN             || base.RN,
                };
                // Also back-fill the FIRST row if it was missing bill info
                if (!grp.rows[0].BillNo && filled.BillNo) {
                    grp.rows[0] = {
                        ...grp.rows[0],
                        SupplierName:   filled.SupplierName,
                        BillNo:         filled.BillNo,
                        BillDate:       filled.BillDate,
                        PaymentDueDate: filled.PaymentDueDate,
                        BillAmount:     filled.BillAmount,
                        PaymentStatus:  filled.PaymentStatus,
                        RN:             filled.RN,
                    };
                }
                grp.rows.push(filled);
            }
        });
        return groups;
    }, [filtered]);

    const grandTotals = useMemo(() => {
        return groupedRows.reduce((acc, group) => {
            const bill    = group.rows[0];
            const lastRow = group.rows[group.rows.length - 1];
            const paidSum = group.rows.reduce((s, r) => s + (parseFloat(r.PaidAmount) || 0), 0);
            return {
                billAmount:      acc.billAmount      + (parseFloat(bill.BillAmount)         || 0),
                paidAmount:      acc.paidAmount      + paidSum,
                totalPaidAmount: acc.totalPaidAmount + (parseFloat(lastRow.TotalPaidAmount) || 0),
                balanceAmount:   acc.balanceAmount   + (parseFloat(lastRow.BalanceAmount)   || 0),
            };
        }, { billAmount: 0, paidAmount: 0, totalPaidAmount: 0, balanceAmount: 0 });
    }, [groupedRows]);

    const handleExport = async () => {
        logAudit(AUDIT_ACTION.EXPORT, 'VendorBillWisePaymentDetails', 0);
        if (!filtered.length) return;
        const headers = ['#', 'Supplier', 'Bill No', 'Bill Date', 'Due Date',
            'Bill Amount', 'Pay No', 'Pay Date', 'Pay Type',
            'Paid Amount', 'Total Paid', 'Balance', 'Status'];
        const rows = filtered.map((r, i) => [
            i + 1, r.SupplierName, r.BillNo, fmtDate(r.BillDate), fmtDate(r.PaymentDueDate),
            parseFloat(r.BillAmount) || 0, r.PaymentNo || '-', fmtDate(r.PaymentDate),
            r.PaymentTypeName || '-', parseFloat(r.PaidAmount) || 0,
            parseFloat(r.TotalPaidAmount) || 0, parseFloat(r.BalanceAmount) || 0, r.PaymentStatus || '-',
        ]);
        await exportStyledExcel({
            reportTitle: 'VENDOR BILL WISE PAYMENT DETAILS',
            sheetName: 'Vendor Bill Payment Details',
            fileName: `VendorBillWisePaymentDetails_${fromDate}_${toDate}.xlsx`,
            companyName, username, fromDate, toDate, headers, rows,
            totalsRow: ['', '', '', '', 'Totals', fmtN(grandTotals.billAmount), '', '', '',
                fmtN(grandTotals.paidAmount), fmtN(grandTotals.totalPaidAmount), fmtN(grandTotals.balanceAmount), ''],
            columnWidths: [4, 22, 12, 12, 12, 14, 10, 12, 14, 14, 14, 14, 12],
        });
    };

    const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #80cbc4', color: '#fff', background: PRIMARY_MID };
    const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading vendor bill wise payment details..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Vendor Bill Wise Payment Details
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>Payable Due</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    <SearchableDropdown
                        value={vendorId}
                        onChange={v => setVendorId(v)}
                        options={vendors.map(v => ({ value: String(v.MasterId || v.Id), label: v.Name }))}
                        placeholder="Select Vendor"
                        minWidth={180}
                        primaryColor={PRIMARY}
                    />
                    <input placeholder="Search bill / status..." value={search} onChange={e => setSearch(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 150, outline: 'none' }} />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Summary bar */}
                {fetched && (
                    <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {fromDate} to {toDate} &nbsp;|&nbsp; {groupedRows.length} bills
                        {search && ` (filtered from ${data.length} records)`}
                        &nbsp;|&nbsp; Balance: <strong style={{ color: '#b91c1c' }}>{fmtN(grandTotals.balanceAmount)}</strong>
                    </div>
                )}

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 140px)' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 900, width: '100%' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                            <tr>
                                <th style={{ ...thBase, textAlign: 'center', width: 32 }}>#</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 160 }}>Supplier</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 100 }}>Bill No</th>
                                <th style={{ ...thBase, textAlign: 'center' }}>Bill Date</th>
                                <th style={{ ...thBase, textAlign: 'center' }}>Due Date</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Bill Amount</th>
                                <th style={{ ...thBase, textAlign: 'center' }}>Pay No</th>
                                <th style={{ ...thBase, textAlign: 'center' }}>Pay Date</th>
                                <th style={{ ...thBase, textAlign: 'left' }}>Pay Type</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Paid Amount</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Total Paid</th>
                                <th style={{ ...thBase, textAlign: 'right', background: '#0a5f6b' }}>Balance</th>
                                <th style={{ ...thBase, textAlign: 'center' }}>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {groupedRows.length === 0 ? (
                                <tr>
                                    <td colSpan={13} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                                        {!vendorId ? 'Select a vendor to load report' : fetched ? 'No records found' : 'Select date range and click Refresh'}
                                    </td>
                                </tr>
                            ) : groupedRows.flatMap((group, gIdx) => {
                                const bill      = group.rows[0];
                                const lastRow   = group.rows[group.rows.length - 1];
                                const rowBg     = gIdx % 2 === 0 ? '#fff' : PRIMARY_LT;
                                const statusKey = (lastRow.PaymentStatus || '').toUpperCase();
                                const statusSt  = STATUS_COLORS[statusKey] || { bg: '#f1f5f9', color: '#475569' };
                                const isMulti   = group.rows.length > 1;

                                return group.rows.map((r, rIdx) => {
                                    const isFirst = rIdx === 0;
                                    const isLast  = rIdx === group.rows.length - 1;

                                    // Top border only on first row of each group
                                    const topBorder = isFirst ? '2px solid #b2dfdb' : undefined;

                                    // Sub-payment rows (rIdx > 0) get a subtle indent indicator
                                    const payBg = !isFirst ? (gIdx % 2 === 0 ? '#f8fafc' : '#d9f2f5') : rowBg;

                                    return (
                                        <tr key={`${group.key}-${rIdx}`} style={{ background: payBg }}>

                                            {/* # */}
                                            <td style={{ ...tdBase, textAlign: 'center', color: '#64748b', fontWeight: 600,
                                                borderTop: topBorder, verticalAlign: 'middle',
                                                borderBottom: isLast && isMulti ? '2px solid #b2dfdb' : undefined }}>
                                                {isFirst ? (bill.RN || gIdx + 1) : ''}
                                            </td>

                                            {/* Supplier */}
                                            <td style={{ ...tdBase, fontWeight: isFirst ? 600 : 400, color: isFirst ? '#1e293b' : '#64748b',
                                                borderTop: topBorder, verticalAlign: 'middle',
                                                borderBottom: isLast && isMulti ? '2px solid #b2dfdb' : undefined }}>
                                                {isFirst ? (bill.SupplierName || '-') : ''}
                                            </td>

                                            {/* Bill No */}
                                            <td style={{ ...tdBase, fontWeight: isFirst ? 700 : 400, color: isFirst ? PRIMARY : '#94a3b8',
                                                borderTop: topBorder, verticalAlign: 'middle',
                                                borderBottom: isLast && isMulti ? '2px solid #b2dfdb' : undefined }}>
                                                {isFirst ? (bill.BillNo || '-') : ''}
                                            </td>

                                            {/* Bill Date */}
                                            <td style={{ ...tdBase, textAlign: 'center',
                                                borderTop: topBorder, verticalAlign: 'middle',
                                                borderBottom: isLast && isMulti ? '2px solid #b2dfdb' : undefined }}>
                                                {isFirst ? fmtDate(bill.BillDate) : ''}
                                            </td>

                                            {/* Due Date */}
                                            <td style={{ ...tdBase, textAlign: 'center',
                                                borderTop: topBorder, verticalAlign: 'middle',
                                                borderBottom: isLast && isMulti ? '2px solid #b2dfdb' : undefined }}>
                                                {isFirst ? fmtDate(bill.PaymentDueDate) : ''}
                                            </td>

                                            {/* Bill Amount — show only on first row */}
                                            <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#1e293b',
                                                borderTop: topBorder, verticalAlign: 'middle',
                                                borderBottom: isLast && isMulti ? '2px solid #b2dfdb' : undefined }}>
                                                {isFirst ? fmtN(bill.BillAmount) : ''}
                                            </td>

                                            {/* Pay No */}
                                            <td style={{ ...tdBase, textAlign: 'center', color: PRIMARY, fontWeight: 600,
                                                borderTop: topBorder,
                                                borderBottom: isLast && isMulti ? '2px solid #b2dfdb' : undefined,
                                                paddingLeft: !isFirst ? 18 : undefined }}>
                                                {r.PaymentNo || '-'}
                                            </td>

                                            {/* Pay Date */}
                                            <td style={{ ...tdBase, textAlign: 'center',
                                                borderTop: topBorder,
                                                borderBottom: isLast && isMulti ? '2px solid #b2dfdb' : undefined }}>
                                                {fmtDate(r.PaymentDate)}
                                            </td>

                                            {/* Pay Type */}
                                            <td style={{ ...tdBase,
                                                borderTop: topBorder,
                                                borderBottom: isLast && isMulti ? '2px solid #b2dfdb' : undefined }}>
                                                {r.PaymentTypeName
                                                    ? <span style={{ padding: '2px 7px', borderRadius: 8, fontSize: 10, fontWeight: 600, background: PRIMARY_LT, color: PRIMARY }}>{r.PaymentTypeName}</span>
                                                    : '-'}
                                            </td>

                                            {/* Paid Amount */}
                                            <td style={{ ...tdBase, textAlign: 'right', color: '#059669', fontWeight: 600,
                                                borderTop: topBorder,
                                                borderBottom: isLast && isMulti ? '2px solid #b2dfdb' : undefined }}>
                                                {fmtN(r.PaidAmount)}
                                            </td>

                                            {/* Total Paid — show only on last row of group */}
                                            <td style={{ ...tdBase, textAlign: 'right', color: '#059669',
                                                borderTop: topBorder,
                                                borderBottom: isLast && isMulti ? '2px solid #b2dfdb' : undefined }}>
                                                {isLast ? fmtN(lastRow.TotalPaidAmount) : ''}
                                            </td>

                                            {/* Balance — show only on last row of group */}
                                            <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#b91c1c', background: isLast ? '#fff5f5' : undefined,
                                                borderTop: topBorder,
                                                borderBottom: isLast && isMulti ? '2px solid #b2dfdb' : undefined }}>
                                                {isLast ? fmtN(lastRow.BalanceAmount) : ''}
                                            </td>

                                            {/* Status — show only on last row of group */}
                                            <td style={{ ...tdBase, textAlign: 'center', verticalAlign: 'middle',
                                                borderTop: topBorder,
                                                borderBottom: isLast && isMulti ? '2px solid #b2dfdb' : undefined }}>
                                                {isLast
                                                    ? <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, background: statusSt.bg, color: statusSt.color }}>
                                                        {lastRow.PaymentStatus || '-'}
                                                      </span>
                                                    : ''}
                                            </td>
                                        </tr>
                                    );
                                });
                            })}
                        </tbody>

                        {/* Grand totals footer */}
                        {groupedRows.length > 0 && (
                            <tfoot>
                                <tr style={{ background: '#b2ebf2', fontWeight: 700 }}>
                                    <td colSpan={5} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: PRIMARY }}>{fmtN(grandTotals.billAmount)}</td>
                                    <td colSpan={3} style={{ ...tdBase }} />
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmtN(grandTotals.paidAmount)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#059669' }}>{fmtN(grandTotals.totalPaidAmount)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#b91c1c', background: '#fecaca' }}>{fmtN(grandTotals.balanceAmount)}</td>
                                    <td style={{ ...tdBase }} />
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default VendorBillWisePaymentDetails;
